import * as React from 'react';
import {ChangeEvent, Component} from 'react';
import debounce from 'lodash/debounce';
import {customSort} from "../utils/customSort";
import {IEntitySearchResultNode} from "../types";

interface IProps {
    onSelect: (node: IEntitySearchResultNode) => void;
    showLoader: boolean;
    endpoint?: string;
    inputClass?: string;
    resultClass?: string;
    resultsClass?: string;
    resultFocusClass?: string;
    inputPosition?: string;
    inputPlaceholder?: string;
    source?: string;
    search: (query: string, callback: any) => void;
    getName: (result: any) => string;
    getSubtitle: (node: IEntitySearchResultNode) => string|undefined;
    proximity?: string;
    bbox?: any;
    types?: string;
    onSuggest?: (results: any[]) => void;
    focusOnMount?: boolean;
}

/**
 * Geocoder component: connects to Mapbox.com Geocoding API
 * and provides an autocompleting interface for finding locations.
 */
export class Search extends Component<IProps, any> {
    input = React.createRef<HTMLInputElement>();
    state: any = {
        results: [],
        focus: null,
        loading: false,
        searchTime: new Date()
    };

    // 1. First change backslashed backslashes back to single backslashes.
    //
    // 2. Find backslash single quote combinations (i.e. "\'") and convert them to
    //    single quotes (i.e. "'"). Use a while loop to be sure.
    //    Now there should be no single quotes preceded by backslashes left.
    //
    // 3. Find all backslashes and convert them to double backslashes.
    //    Now all characters preceded by a backslash should be in their
    //    original state, while all backslashed single quotes are
    //    just single quotes. E.g. "\1ABC'ABC\2\q"
    //
    // 5. Find all single quotes and backslash them.
    //    (Note: There are no single quotes preceded by a backslash, so
    //    it is not possible to have a backslash which would hide a
    //    backslash preceding a single quote.)
    search = debounce((value: string) => {
        this.setState({loading:true});
        // let newValue = value.replace(/\\\\/g, '\\');
        // newValue = newValue.replace("\'", "'");
        // newValue = newValue.replace('\\', '\\\\');
        // newValue = newValue.replace("'", "\'");
        // newValue = escapeSpecialChars(newValue);
        this.props.search(value, this.onResult);
    }, 100);

    componentDidMount() {
        const node = this.input.current;
        if (this.props.focusOnMount && node) {
            node.focus();
            // ReactDOM.findDOMNode(this.refs.input).focus();
        }
    }

    cancel = () => {
        this.setState({
            results: [],
            focus: null,
            loading:false,
            showNoResults: false
        });
        const node = this.input.current;
        if (node) {
            node.value = '';
        }
        // ReactDOM.findDOMNode(this.refs.input).value = '';
    }

    // chars:
    // !
    // &
    // (
    // )
    // <
    // ‘
    // :
    // “
    // \
    // I

    //

    onInput = (e: ChangeEvent<HTMLInputElement>) => {
        // this.setState({loading:true});
        let value = e.target.value.trim().replace(/\s\s+/g, ' ');
        value = value.replace(/[!&()‘<>:"“\\|]/g, '');
        if (value === '') {
            this.setState({
                results: [],
                focus: null,
                loading:false
            });
        } else {
            // this.search(value);
        }
    };

    moveFocus(dir: number) {
        if(this.state.loading) return;
        this.setState({
            focus: this.state.focus === null ?
                0 : Math.max(0,
                    Math.min(
                        this.state.results.length - 1,
                        this.state.focus + dir))
        });
    }

    acceptFocus() {
        if (this.state.focus !== null) {
            this.props.onSelect(this.state.results[this.state.focus]);
        }
    }

    onKeyDown = (e: any) => {
        switch (e.which) {
            // up
            case 38:
                e.preventDefault();
                this.moveFocus(-1);
                break;
            // down
            case 40:
                this.moveFocus(1);
                break;
            // accept
            case 13:
                // if( this.state.results.length > 0 && this.state.focus == null) {
                //     this.clickOption(this.state.results[0],0);
                // }
                const value = e.target.value;
                this.search(value);
                // this.acceptFocus();
                break;
        }
    }

    onResult = (err: any, res: any, body: any[], searchTime: Date) => {
        // searchTime is compared with the last search to set the state
        // to ensure that a slow xhr response does not scramble the
        // sequence of autocomplete display.
        if (!err && body && this.state.searchTime <= searchTime) {
            this.setState({
                searchTime: searchTime,
                loading: false,
                results: body,
                focus: null,
                showNoResults: body.length === 0
            });
            this.props.onSuggest && this.props.onSuggest(this.state.results);
        }

        // if (body.length === 0) {
        //     setTimeout(() =>
        //         this.setState({
        //             showNoResults: false
        //         }),
        //         1000
        //     )
        // }
    }

    clickOption(place: any, listLocation: boolean) {
        this.props.onSelect(place);
        this.setState({focus: listLocation});
        // focus on the input after click to maintain key traversal
        // ReactDOM.findDOMNode(this.refs.input).focus();
        this.cancel();
        return false;
    }

    getSortTitle = (result: IEntitySearchResultNode) => {
        return `${this.props.getSubtitle(result)}-${this.props.getName(result)}`;
    }

    sortResults = (resultA: IEntitySearchResultNode, resultB: IEntitySearchResultNode) => {
        const nameA = this.getSortTitle(resultA);
        const nameB = this.getSortTitle(resultB);
        return customSort(nameA, nameB);
    }

    render () {
        const input = <input
            ref={this.input}
            // ref='input'
            className={this.props.inputClass}
            onInput={this.onInput}
            onKeyDown={this.onKeyDown}
            placeholder={this.props.inputPlaceholder}
            type='text' />;
        return (
            <div className={'mapboxgl-ctrl-geocoder mapboxgl-ctrl'}>
                {this.props.inputPosition === 'top' && <span className="geocoder-icon geocoder-icon-search"/>}
                {this.props.inputPosition === 'top' && input}
                {this.state.results.length > 0 && (
                    <ul className={`${this.props.showLoader && this.state.loading ? 'loading' : ''} ${this.props.resultsClass}`}>
                        {this.state.results.sort(this.sortResults).map((result: IEntitySearchResultNode, i: number) => (
                            <li key={i}>
                                <a
                                   onClick={this.clickOption.bind(this, result, !!i)}
                                   className={this.props.resultClass + ' ' + (i === this.state.focus ? this.props.resultFocusClass : '')}
                                   key={result.fullUid}>
                                    {this.props.getSubtitle &&
                                        <div
                                            title={this.props.getSubtitle(result)}>
                                            <strong>{this.props.getSubtitle(result)}</strong>
                                        </div>}
                                    {this.props.getName ?
                                        <div title={this.props.getName(result)}>{this.props.getName(result)}</div> :
                                        <div title={result.name}>result.name</div>
                                    }
                                </a>
                            </li>
                        ))}
                    </ul>
                )}
                {this.state.showNoResults &&
                <ul className={`${this.props.resultsClass}`}>
                    <li>
                        <div className={this.props.resultClass}>
                            No Results
                        </div>
                    </li>
                </ul>
                }
                <div className='geocoder-pin-right'>
                    {(this.state.results.length > 0 || this.state.showNoResults) &&
                        <button className='geocoder-icon geocoder-icon-close'
                            onClick={this.cancel}
                            aria-label='Clear'
                            style={{display: 'block'}}/>
                    }
                    {this.state.loading &&
                        <span className='geocoder-icon geocoder-icon-loading' style={{display: 'block'}}/>
                    }
                </div>
                {this.props.inputPosition === 'bottom' && <span className='geocoder-icon geocoder-icon-search'/>}
                {this.props.inputPosition === 'bottom' && input}
            </div>
        );
    }

}
